import { groupBy, mapValues } from "lodash";
import { createContext, useContext, useState, useCallback } from "react";
import { useMemo } from "react";
import toast from "react-hot-toast";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import {
  CalendarIcon,
  CalendarUserIcon,
  CapsuleIcon,
  CardiologyIcon,
  CartIcon,
  CoinIcon,
  DocumentIcon,
  MessageIcon,
  PackageIcon,
  PinpaperFilledIcon,
  ReportsIcon,
  SettingsIcon,
  ShareIcon,
  ShieldUserIcon,
  ShoppingBagIcon,
  TrendingUpIcon,
  UserQuestionIcon,
  UsersIcon,
  UserWarningIcon,
  WaitlistIcon,
  WarningCircleIcon,
} from "@/components/common/icons";
import MoneyIcon from "@/components/common/icons/moneyIcon";
import AppointmentComplianceHubIssueCounterNotificationDot from "@/components/common/navigation/appointmentComplianceHubIssueCounterNotificationDot";
import { AppointmentOverdueChartsNotificationsDot } from "@/components/common/navigation/appointmentOverdueChartsNotificationsDot";
import ComplianceHubIssueCounterNotificationDot from "@/components/common/navigation/complianceHubIssueCounterNotificationDot";
import { OverdueChartsNotificationsDot } from "@/components/common/navigation/overdueChartsNotificationsDot";
import PendingAdverseReactionsNotificationDot from "@/components/common/navigation/pendingAdverseReactionsNotificationDot";
import { MOXIE_HELP_DESK_URL, MOXIE_SHOPIFY_STORE_URL } from "@/config";
import { usePayrollPreliminaryInfoQuery } from "@/graphql/queries/payroll.graphql.types";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import useFeatureManagement from "@/hooks/common/useFeatureManagement";
import { useMedspaId } from "@/hooks/common/useMedspaId";
import useMoxieBalancePermissions from "@/hooks/common/useMoxieBalancePermissions";
import { useIsEligibleForSelfReviewGfe } from "@/hooks/user/useIsEligibleForSelfReviewGfe";
import { FeatureFlags } from "@/utils/featureFlags";

type AppNavContextType = {
  navData: NavDataType;
  navCategoryCollapsedState: Record<string, boolean>;
  toggleNavCategory: (category: string) => void;
  loading: boolean;
};

export const AppNavContext = createContext<AppNavContextType | null>(null);

export const NAV_ITEM_CATEGORIES: Record<string, string> = {
  CLIENT_CARE: "client_care",
  GROWTH: "growth",
  OPERATIONS_AND_FINANCE: "operations_and_finance",
  OTHER: "other", // This category is the items without group and will be shown last without category
} as const;

export const NAV_ITEM_CATEGORY_LABELS: Record<string, string> = {
  [NAV_ITEM_CATEGORIES.CLIENT_CARE]: "Client Care",
  [NAV_ITEM_CATEGORIES.GROWTH]: "Growth",
  [NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE]: "Operations & Finance",
  [NAV_ITEM_CATEGORIES.OTHER]: "Other",
} as const;

export const NAV_ITEM_CATEGORY_ORDER: Record<string, number> = {
  [NAV_ITEM_CATEGORIES.CLIENT_CARE]: 1,
  [NAV_ITEM_CATEGORIES.GROWTH]: 2,
  [NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE]: 3,
  [NAV_ITEM_CATEGORIES.OTHER]: 4,
} as const;

type NavItem = {
  title: string;
  path?: string;
  getIsActive?: (url: string) => boolean;
  onClick?: () => void;
  icon: React.ElementType;
  isExternal?: boolean;
  notifications?: React.ElementType;
  target?: string;
  navItemCategory?: string;
};

export type NavDataType = {
  subheader?: string;
  items: NavItem[];
}[];

const DedicatedSettingsItem = {
  SERVICES_AND_PRODUCTS: "services-and-products",
  CLINICAL_DOCUMENTS: "clinical-documents",
  BILLING: "billing",
};

type NavigationPathBuilder = (medspaId: string) => string;

export const NAVIGATION_PATHS: Record<string, NavigationPathBuilder> = {
  REVIEW_GFES: (medspaId: string) => `/${medspaId}/review/gfes`,
  COMPLIANCE_HUB: (medspaId: string) => `/${medspaId}/compliance-hub`,
  SETTINGS: (medspaId: string) => `/${medspaId}/settings`,
  CLIENTS: (medspaId: string) => `/${medspaId}/clients`,
  VISITS: (medspaId: string) => `/${medspaId}/visits`,
  APPOINTMENTS: (medspaId: string) => `/${medspaId}/appointments`,
  MESSAGES: (medspaId: string) => `/${medspaId}/messages`,
  CHARTS: (medspaId: string) => `/${medspaId}/charts`,
  REVIEW_CHARTS: (medspaId: string) => `/${medspaId}/review/charts`,
  INVENTORY: (medspaId: string) => `/${medspaId}/inventory/service-products`,
  SALES: (medspaId: string) => `/${medspaId}/sales/all-invoices`,
  SALES_CASH_REPORT: (medspaId: string) => `/${medspaId}/sales/cash-report`,
  WAITLIST: (medspaId: string) => `/${medspaId}/waitlist`,
  BALANCE: (medspaId: string) => `/${medspaId}/balance`,
  PRACTICE_ACCELERATOR: (medspaId: string) =>
    `/${medspaId}/practice-accelerator`,
  SUPPLIES: (medspaId: string) => `/${medspaId}/supplies`,
  MARKETING_CONTENT_VAULT: (medspaId: string) =>
    `/${medspaId}/marketing/content-vault`,
  MARKETING_CONTACTS: (medspaId: string) => `/${medspaId}/marketing/contacts`,
  BOOKKEEPING: (medspaId: string) => `/${medspaId}/bookkeeping`,
  REPORTS: (medspaId: string) => `/${medspaId}/reports`,
  SERVICES_AND_PRODUCTS: (medspaId: string) =>
    `/${medspaId}/settings/${DedicatedSettingsItem.SERVICES_AND_PRODUCTS}`,
  CLINICAL_DOCUMENTS: (medspaId: string) =>
    `/${medspaId}/settings/medical-director/${DedicatedSettingsItem.CLINICAL_DOCUMENTS}`,
  PROVIDER_MEETINGS: (medspaId: string) => `/${medspaId}/provider-meetings`,
  ADVERSE_REACTIONS: (medspaId: string) => `/${medspaId}/adverse-reactions`,
  BILLING: (medspaId: string) =>
    `/${medspaId}/settings/medical-director/${DedicatedSettingsItem.BILLING}`,
  SCRIPTS: (medspaId: string) => `/${medspaId}/scripts`,
  RUN_PAYROLL: (medspaId: string) => `/${medspaId}/payroll/run_payroll`,
  BASIC_INFO: (medspaId: string) => `/${medspaId}/payroll/basic_info`,
  WORK_SHIFTS: (medspaId: string) => `/${medspaId}/payroll/work_shifts`,
} as const satisfies Record<string, NavigationPathBuilder>;

const filterForVisibleItems = (navData: NavDataType): NavDataType =>
  navData.map((group) => ({
    ...group,
    items: group.items.filter(Boolean),
  }));

const groupByCategory = (
  navItems: NavItem[],
  intercomV1Enabled: boolean
): NavDataType => {
  const categorized = intercomV1Enabled
    ? groupBy(navItems, (item) => item.navItemCategory || "other")
    : { other: navItems };

  return Object.entries(categorized)
    .sort(([a], [b]) => NAV_ITEM_CATEGORY_ORDER[a] - NAV_ITEM_CATEGORY_ORDER[b])
    .map(([category, groups]) => ({
      subheader: NAV_ITEM_CATEGORY_LABELS[category],
      items: groups,
    }));
};

export function useAppNavContextState() {
  const { hasUrlPermission, hasFeaturePermission, hasAnyOfFeaturePermissions } =
    useUser();
  const medspa = useMedspaId();

  const [navCategoryCollapsedState, setNavCategoryCollapsedState] = useState<
    Record<string, boolean>
  >(mapValues(NAV_ITEM_CATEGORIES, () => false));

  const toggleNavCategory = useCallback((category: string) => {
    setNavCategoryCollapsedState((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  }, []);

  const {
    balanceEnabled,
    scriptsNavItemEnabled,
    practiceAcceleratorV1Enabled,
    commandAiEnabled,
    shopifyPlusEnabled,
    payrollIntegrationV1Enabled,
    realTimeSalesV1Enabled,
    cashDrawerV1Enabled,
    newAppointmentModelEnabled,
    intercomV1Enabled,
    featureFlagsLoading,
    waitlistV1Enabled,
  } = useFeatureFlags();

  const { waitlistEnabled } = useFeatureManagement();

  const isEligibleForSelfGfeReview = useIsEligibleForSelfReviewGfe();
  const { showBalance } = useMoxieBalancePermissions();
  const canViewSuppliesPage = hasFeaturePermission(
    FeaturePermission.VIEW_SUPPLIES_PAGE
  );

  const canViewPracticeAccelerator = hasFeaturePermission(
    FeaturePermission.VIEW_PRACTICE_ACCELERATOR
  );

  const includeReviewGfeItem =
    isEligibleForSelfGfeReview ||
    !hasFeaturePermission(FeaturePermission.IS_PROVIDER);

  const { data: payrollPreliminaryInfo } = usePayrollPreliminaryInfoQuery({
    variables: {
      medspaId: medspa,
    },
    skip: !payrollIntegrationV1Enabled || !medspa,
  });

  const hasRunPayrollPermission = hasFeaturePermission(
    FeaturePermission.MANAGE_PAYROLL
  );
  const isPayrollCompanyRegistered =
    payrollPreliminaryInfo?.payroll?.isCompanyRegisteredInCheck;
  const isCurrentUserRegisteredInCheck =
    payrollPreliminaryInfo?.payroll?.isCurrentUserRegisteredInCheck;

  // Payroll should be visible ONLY to the employer (provider-owner with MANAGE_PAYROLL permission)
  //  if a payroll company is registered in Check and to the employees if they are registered in Check
  //  or if they have the ability to view own/others' work shifts
  const shouldSeePayrollTabBecauseOfClockin = hasAnyOfFeaturePermissions([
    FeaturePermission.TRACK_WORK_SHIFTS,
    FeaturePermission.VIEW_OTHER_EMPLOYEES_WORK_SHIFTS,
  ]);
  const canViewPayroll =
    (hasRunPayrollPermission && isPayrollCompanyRegistered) ||
    isCurrentUserRegisteredInCheck ||
    shouldSeePayrollTabBecauseOfClockin;

  const { enabledFeatures } = useFeatureFlags();
  const marketingDefaultTab = enabledFeatures.includes(
    FeatureFlags.MARKETING_CONTACTS_V1
  )
    ? NAVIGATION_PATHS.MARKETING_CONTACTS(medspa)
    : NAVIGATION_PATHS.MARKETING_CONTENT_VAULT(medspa);

  // Check if user has access to any sales tabs
  const canViewAllInvoices =
    realTimeSalesV1Enabled &&
    hasFeaturePermission(FeaturePermission.VIEW_ALL_INVOICES);
  const canViewCashReport =
    cashDrawerV1Enabled &&
    hasFeaturePermission(FeaturePermission.VIEW_CASH_REPORT);

  const canViewAnySalesTab = canViewAllInvoices || canViewCashReport;

  const salesDefaultTab = canViewAllInvoices
    ? NAVIGATION_PATHS.SALES(medspa)
    : NAVIGATION_PATHS.SALES_CASH_REPORT(medspa);

  const navData = useMemo(() => {
    const ReviewerGfeItem: NavItem = {
      title: "GFEs",
      path: NAVIGATION_PATHS.REVIEW_GFES(medspa),
      getIsActive: (url) =>
        url.includes("review/gfe") || url.includes("review/sync-gfe"),
      icon: CardiologyIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const ComplianceHubItem: NavItem = {
      title: "Compliance Hub",
      path: NAVIGATION_PATHS.COMPLIANCE_HUB(medspa),
      icon: ShieldUserIcon,
      notifications: newAppointmentModelEnabled
        ? AppointmentComplianceHubIssueCounterNotificationDot
        : ComplianceHubIssueCounterNotificationDot,
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const SettingsItem: NavItem = {
      title: "Settings",
      path: NAVIGATION_PATHS.SETTINGS(medspa),
      getIsActive: (url) => {
        const isDedicatedItem = Object.values(DedicatedSettingsItem).some(
          (item) => url.includes(item)
        );

        return isDedicatedItem ? false : url.includes("settings");
      },
      icon: SettingsIcon,
    };

    const ClientsItem: NavItem = {
      title: "Clients",
      path: NAVIGATION_PATHS.CLIENTS(medspa),
      icon: UsersIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const ScheduleItemOld: NavItem = {
      title: "Schedule",
      path: NAVIGATION_PATHS.VISITS(medspa),
      icon: CalendarIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const ScheduleItemNew: NavItem = {
      title: "Schedule",
      path: NAVIGATION_PATHS.APPOINTMENTS(medspa),
      icon: CalendarIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const ScheduleItem = newAppointmentModelEnabled
      ? ScheduleItemNew
      : ScheduleItemOld;

    const MessagesItem: NavItem = {
      title: "Messages",
      path: NAVIGATION_PATHS.MESSAGES(medspa),
      icon: MessageIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const MedSpaChartsItem: NavItem = {
      title: "Charts",
      path: NAVIGATION_PATHS.CHARTS(medspa),
      icon: PinpaperFilledIcon,
      notifications: newAppointmentModelEnabled
        ? AppointmentOverdueChartsNotificationsDot
        : OverdueChartsNotificationsDot,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const ChartsReviewItem: NavItem = {
      title: "Charts",
      path: NAVIGATION_PATHS.REVIEW_CHARTS(medspa),
      getIsActive: (url) =>
        url.includes(`/review/charts`) || /\/visits\/\d+\/chart/.test(url), // match /visits/:id/chart
      icon: PinpaperFilledIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const InventoryItem: NavItem = {
      title: "Inventory",
      path: NAVIGATION_PATHS.INVENTORY(medspa),
      getIsActive: (url) => url.includes("inventory"),
      icon: PackageIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const SalesItem: NavItem = {
      title: "Sales",
      path: salesDefaultTab,
      getIsActive: (url) => url.includes("sales"),
      icon: ShoppingBagIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const WaitlistItem: NavItem = {
      title: "Waitlist",
      path: NAVIGATION_PATHS.WAITLIST(medspa),
      icon: WaitlistIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const BalanceItem: NavItem = {
      title: "Balance",
      path: NAVIGATION_PATHS.BALANCE(medspa),
      icon: CoinIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const PracticeAcceleratorItem: NavItem = {
      title: "Practice Accelerator",
      path: NAVIGATION_PATHS.PRACTICE_ACCELERATOR(medspa),
      icon: TrendingUpIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.GROWTH,
    };

    const SuppliesItem: NavItem = {
      title: "Supplies",
      path: shopifyPlusEnabled
        ? MOXIE_SHOPIFY_STORE_URL
        : NAVIGATION_PATHS.SUPPLIES(medspa),
      icon: CartIcon,
      isExternal: shopifyPlusEnabled,
      target: shopifyPlusEnabled ? "_blank" : undefined,
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const MarketingItem: NavItem = {
      title: "Marketing",
      path: marketingDefaultTab,
      getIsActive: (url) => url.includes("/marketing/"),
      icon: ShareIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.GROWTH,
    };

    const BookkeepingItem: NavItem = {
      title: "Bookkeeping",
      path: NAVIGATION_PATHS.BOOKKEEPING(medspa),
      icon: MoneyIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const ReportsItem: NavItem = {
      title: "Reports",
      path: NAVIGATION_PATHS.REPORTS(medspa),
      icon: ReportsIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const ClinicalDocumentsItem: NavItem = {
      title: "Clinical Documents",
      path: NAVIGATION_PATHS.CLINICAL_DOCUMENTS(medspa),
      icon: DocumentIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const ProviderMeetingsItem: NavItem = {
      title: "Provider Meetings",
      path: NAVIGATION_PATHS.PROVIDER_MEETINGS(medspa),
      icon: CalendarUserIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const AdverseReactionsItem: NavItem = {
      title: "Adverse reactions",
      path: NAVIGATION_PATHS.ADVERSE_REACTIONS(medspa),
      icon: UserWarningIcon,
      notifications: PendingAdverseReactionsNotificationDot,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const BillingItem: NavItem = {
      title: "Billing",
      path: NAVIGATION_PATHS.BILLING(medspa),
      icon: CoinIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const ScriptsItem: NavItem = {
      title: "Scripts",
      path: NAVIGATION_PATHS.SCRIPTS(medspa),
      icon: CapsuleIcon,
      navItemCategory: NAV_ITEM_CATEGORIES.CLIENT_CARE,
    };

    const HelpDeskItems: NavItem[] = [
      // New Help Desk button, powered by CommandAi HelpHub
      commandAiEnabled &&
        !intercomV1Enabled && {
          title: "Help Desk",
          onClick: () => {
            if (window.CommandBar) {
              window.CommandBar.toggleHelpHub();
            } else {
              toast.error(
                "Help Desk is not compatible with this browser. Please try another device or reach out to your PSM for assistance."
              );
            }
          },
          icon: UserQuestionIcon,
        },
      // Redirect to old Help Desk, powered by public Hubspot Knowledge Base
      !commandAiEnabled &&
        !intercomV1Enabled && {
          title: "Help Desk",
          path: MOXIE_HELP_DESK_URL,
          isExternal: true,
          icon: UserQuestionIcon,
        },
    ];

    const BugReportItems: NavItem[] = [
      !intercomV1Enabled && {
        title: "Report a Bug",
        onClick: () => {
          window.birdeatsbug.trigger();
        },
        icon: WarningCircleIcon,
      },
    ];

    const PayrollItem: NavItem = {
      title: "Payroll",
      path: hasRunPayrollPermission
        ? NAVIGATION_PATHS.RUN_PAYROLL(medspa)
        : shouldSeePayrollTabBecauseOfClockin
          ? NAVIGATION_PATHS.WORK_SHIFTS(medspa)
          : NAVIGATION_PATHS.BASIC_INFO(medspa),
      icon: CoinIcon,
      getIsActive: (url) => /^\/\d+\/payroll\//.test(url),
      navItemCategory: NAV_ITEM_CATEGORIES.OPERATIONS_AND_FINANCE,
    };

    const restrictedNavDataItems: NavItem[] = [
      ScheduleItem,
      ClientsItem,
      MessagesItem,
      MedSpaChartsItem,
      waitlistV1Enabled && waitlistEnabled && WaitlistItem,
      includeReviewGfeItem && ReviewerGfeItem,
      InventoryItem,
      canViewAnySalesTab && SalesItem,
      ChartsReviewItem,
      ClinicalDocumentsItem,
      ProviderMeetingsItem,
      AdverseReactionsItem,
      BillingItem,
      scriptsNavItemEnabled && ScriptsItem,
      practiceAcceleratorV1Enabled &&
        canViewPracticeAccelerator &&
        PracticeAcceleratorItem,
      // Must be provider owner or PC owner to see balance page + feature flag enabled
      balanceEnabled && showBalance && BalanceItem,
      canViewSuppliesPage && SuppliesItem,
      MarketingItem,
      BookkeepingItem,
      ReportsItem,
      ComplianceHubItem,
      payrollIntegrationV1Enabled && canViewPayroll && PayrollItem,
      SettingsItem,
    ].filter(Boolean);

    const commonNavDataItems: NavItem[] = [...BugReportItems].filter(Boolean);

    const navItems = [
      ...restrictedNavDataItems.filter((item) => hasUrlPermission(item?.path)),
      ...(hasFeaturePermission(FeaturePermission.VIEW_HELP_DESK)
        ? HelpDeskItems
        : []),
      ...commonNavDataItems,
    ];

    return filterForVisibleItems(groupByCategory(navItems, intercomV1Enabled));
  }, [
    medspa,
    showBalance,
    canViewSuppliesPage,
    includeReviewGfeItem,
    balanceEnabled,
    commandAiEnabled,
    shopifyPlusEnabled,
    payrollIntegrationV1Enabled,
    scriptsNavItemEnabled,
    hasUrlPermission,
    hasFeaturePermission,
    hasRunPayrollPermission,
    canViewPayroll,
    practiceAcceleratorV1Enabled,
    canViewPracticeAccelerator,
    newAppointmentModelEnabled,
    marketingDefaultTab,
    salesDefaultTab,
    canViewAnySalesTab,
    intercomV1Enabled,
    waitlistV1Enabled,
    shouldSeePayrollTabBecauseOfClockin,
    waitlistEnabled,
  ]);

  return {
    navData,
    navCategoryCollapsedState,
    toggleNavCategory,
    loading: featureFlagsLoading,
  };
}

export const AppNavContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const contextValue = useAppNavContextState();

  return (
    <AppNavContext.Provider value={contextValue}>
      {children}
    </AppNavContext.Provider>
  );
};

export const useAppNavContext = () => {
  const context = useContext(AppNavContext);
  if (!context) {
    throw new Error(
      "useAppNavContext must be used within a AppNavContextProvider"
    );
  }

  return context;
};
